import { useEffect } from 'react'
import { isString } from './StringUtil'

export default () => {}

export function isNullOrUndefinedOrEmpty(obj) {
  if (!obj) {
    return true
  }
  const type = Object.prototype.toString.call(obj).toLowerCase()
  if (type.includes('array') && obj.length === 0) {
    return true
  }
  if (type.includes('[object object]') && Object.keys(obj).length === 0) {
    return true
  }
  return false
}

export function isNullOrUndefined(obj) {
  if (obj === null || obj === undefined) {
    return true
  }
  return false
}

export function isNotNullOrUndefinedOrEmpty(obj) {
  return !isNullOrUndefinedOrEmpty(obj)
}

export function deepCopy(obj) {
  const type = Object.prototype.toString.call(obj).toLowerCase()
  if (type.includes('[object object]')) {
    const newObject = JSON.parse(JSON.stringify(obj))
    Object.keys(obj).forEach((k) => {
      if (isFunction(obj[k])) {
        newObject[k] = obj[k]
      }
    })
    return newObject
  }
  if (type.includes('array')) {
    return JSON.parse(JSON.stringify(obj))
  }
  if (type.includes('string')) {
    return String(`${obj}`).valueOf()
  }
  if (type.includes('number')) {
    return Number(`${obj}`).valueOf()
  }
  if (type.includes('boolean')) {
    return Boolean(`${obj}`).valueOf()
  }
  if (type.includes('null')) {
    return null
  }
  if (type.includes('undefined')) {
    return undefined
  }
  return 'Not Handled'
}

export function isFunction(obj) {
  return Object.prototype.toString.call(obj).toLowerCase().includes('function')
}

export function sortObjectProperties(obj) {
  const keys = Object.keys(obj).sort()
  const after = {}
  keys.forEach((key) => {
    after[key] = deepCopy(obj[key])
    delete obj[key]
  })
  keys.forEach((key) => {
    obj[key] = after[key]
  })
  return obj
}

export function awesomeAjaxReTrier(functionToTry, errorStatusThatTriggersRetry) {
  const catchErrorDelay = (retrySeconds) => {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        reject()
      }, retrySeconds * 1000)
    })
  }

  // looping
  let p = Promise.reject() // start in reject because the first "functionToTry" is in a catch
  const retrySeconds = 1
  const retryIntents = 10
  for (let i = 0; i < retryIntents; i++) {
    p = p.then(
      (obj) => {
        return Promise.resolve(obj)
      },
      () => {
        return functionToTry().catch((error) => {
          if (errorStatusThatTriggersRetry == null || error.status === errorStatusThatTriggersRetry) {
            return Promise.reject() // This reject means continue trying
          }
          return Promise.resolve(error) // This when means just ignore the error and stop trying
        })
      }
    )
    p = p.then(
      (obj) => {
        return Promise.resolve(obj)
      },
      () => {
        return catchErrorDelay(retrySeconds)
      }
    )
  }
  // processing and returning promises
  return p.then(
    (obj) => {
      return Promise.resolve(obj)
    },
    () => {
      return Promise.reject()
    }
  )
}

export function isDeepEqual(x, y) {
  // function taken from https://stackoverflow.com/questions/201183/how-to-determine-equality-for-two-javascript-objects

  if (x === null || x === undefined || y === null || y === undefined) {
    return x === y
  }
  // after this just checking type of one would be enough
  if (x.constructor !== y.constructor) {
    return false
  }
  // if they are functions, they should exactly refer to same one (because of closures)
  if (x instanceof Function) {
    return x === y || x.toString() === y.toString()
  }
  // if they are regexps, they should exactly refer to same one (it is hard to better equality check on current ES)
  if (x instanceof RegExp) {
    return x === y
  }
  if (x === y || x.valueOf() === y.valueOf()) {
    return true
  }
  if (Array.isArray(x) && x.length !== y.length) {
    return false
  }

  // if they are dates, they must had equal valueOf
  if (x instanceof Date) {
    return false
  }

  // if they are strictly equal, they both need to be object at least
  if (!(x instanceof Object)) {
    return false
  }
  if (!(y instanceof Object)) {
    return false
  }

  // recursive object equality check
  const p = Object.keys(x)
  return (
    Object.keys(y).every((i) => {
      return p.indexOf(i) !== -1
    }) &&
    p.every((i) => {
      return isDeepEqual(x[i], y[i])
    })
  )
}

// lifted from https://dmitripavlutin.com/how-to-compare-objects-in-javascript/
export function shallowEqual(insetInlineStart, insetInlineEnd) {
  const leftKeys = Object.keys(insetInlineStart)
  const rightKeys = Object.keys(insetInlineEnd)
  if (leftKeys.length !== rightKeys.length) {
    return false
  }

  const equal = leftKeys.every((key) => {
    const leftValue = insetInlineStart[key]
    const rightValue = insetInlineEnd[key]
    if (!leftValue && !rightValue) {
      return true
    }

    return leftValue === rightValue
  })

  return equal
}

/**
 * useKeyPress
 * @param {string} key - the name of the key to respond to, compared against event.key
 * @param {function} action - the action to perform on key press
 */
export function useKeypress(key, action) {
  useEffect(() => {
    function onKeyup(e) {
      if (e.key === key) action()
    }
    window.addEventListener('keyup', onKeyup)
    return () => window.removeEventListener('keyup', onKeyup)
  }, [action, key])
}

export function stringToBoolean(text) {
  if (!isString(text)) {
    return false
  }
  if (isNullOrUndefinedOrEmpty(text)) {
    return false
  }
  if (text.toLowerCase().includes('true')) {
    return true
  }
  return false
}

export function millisToMinutesAndSeconds(millis) {
  const minutes = Math.floor(millis / 60000)
  const seconds = ((millis % 60000) / 1000).toFixed(0)
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
}

export const sleep = (ms) =>
  new Promise((r) => {
    setTimeout(r, ms)
  })
